import { css } from '@emotion/react';
import { motion } from 'framer-motion';
import Image from 'next/legacy/image';
import React, { useId, useState } from 'react';

import { MembershipAlert } from '@/booking-membership/components/MembershipAlert';
import { MembershipSummary } from '@/booking-membership/components/MembershipSummary';
import { useCurrentBookingMembershipDiscountDetail } from '@/booking-membership/hooks/use-current-booking-membership-discount-detail.hook';
import { SummarySectionContainer } from '@/booking/components/SummarySectionContainer';
import { Flex } from '@/box';
import { useActiveBrandConfig } from '@/brand';
import DataTableRow from '@/core/components/layout/DataTableRow.component';
import { CurrencyPreview } from '@/currency-preview';
import type { IFinancialAmountInstance } from '@/finance';
import { CurrencyCode } from '@/finance/interfaces';
import { useTranslation } from '@/i18n';
import { AnchorButton } from '@/ui/controls';
import { DropdownIndicator } from '@/ui/icons';
import { Divider } from '@/ui/layout';
import { useTheme } from '@/ui/theme';
import { BodySmall, Heading } from '@/ui/typography';

import { useDisplayCityTaxOverride } from '../hooks/use-display-city-tax-override';
import { BookingRequest } from '../interfaces';
import { AlternativeCurrencyCheckoutWarning } from './AlternativeCurrencyCheckoutWarning.component';
import {
  BookingBreakdownPrimaryTable,
  BookingBreakdownRoomsTable,
  BookingBreakdownTaxTotalsTable,
} from './BookingBreakdown.component';
import { CityTaxLineItemOverride } from './CityTaxLineItemOverride.component';

interface YourStaySummaryProps extends BookingRequest {
  hotelRoomImageSrc?: string;
  hotelName: string;
  overallTotal: IFinancialAmountInstance | undefined;
  hotelCurrency: CurrencyCode;
  numberOfNights: number;
  totalAverageNightlyAmountAfterTax: IFinancialAmountInstance | undefined;
}

export const YourStaySummary: React.FC<YourStaySummaryProps> = (
  props: YourStaySummaryProps
) => {
  // This should really be a part of a collapsible component.
  // I got halfway there (see 'CollapsibleCard') but for the sake of speed
  // and simplicity I've abandoned temporarily to just add this here.
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { t } = useTranslation();
  const staySummarySectionHeadingId = useId();

  const { keylines, componentProperties } = useTheme();
  const { showTaxTotalInBreakdown, showAveragePricePerNight } =
    useActiveBrandConfig();
  const displayCityTaxOverride = useDisplayCityTaxOverride(props.hotelSlug);

  const { discountDetail, membershipPrice } =
    useCurrentBookingMembershipDiscountDetail();
  const showStayMembershipOverview = discountDetail?.membershipRateCode;

  return (
    <section>
      <div
        css={css({
          overflow: 'hidden',
          borderRadius:
            componentProperties.sidebarCards?.secondary?.borderRadius,
          backgroundColor: 'white',
          border: keylines?.border,
          ['div[role=separator]']: {
            marginLeft: '-32px',
            marginRight: '-32px',
            ...componentProperties.staySummary?.divider,
          },
          ...componentProperties.staySummary,
        })}
        aria-labelledby={staySummarySectionHeadingId}
        // Unmask section in FullStory
        className="fs-unmask"
      >
        <div
          css={css`
            img {
              object-fit: cover;
              height: 270px;
              width: 100%;

              @media all and (max-width: 720px) {
                display: none;
              }
            }
          `}
        >
          {props.hotelRoomImageSrc && (
            <Image
              src={props.hotelRoomImageSrc}
              alt={props.hotelName}
              width={600}
              height={270}
              layout="responsive"
            />
          )}
        </div>
        <div css={css({ lineHeight: '26px' })}>
          <SummarySectionContainer>
            <Heading
              as="h6"
              style="heading6"
              id={staySummarySectionHeadingId}
              css={css({
                ...componentProperties.staySummary?.title,
                ['@media all and (max-width: 730px)']:
                  componentProperties.staySummary?.title?.mobile,
              })}
            >
              {t('bookingSummary:title')}
            </Heading>
          </SummarySectionContainer>
          <Divider />
          <SummarySectionContainer>
            <BookingBreakdownPrimaryTable {...props} showTotalNightCount />
            <Flex justifyContent={'flex-end'}>
              <AnchorButton
                onClick={() => setIsCollapsed((state) => !state)}
                aria-expanded={!isCollapsed}
                data-test="toggle-expand-stay-summary"
              >
                <BodySmall
                  css={css({
                    opacity: 0.53,
                    ...componentProperties.staySummary?.dropdown,
                  })}
                >
                  {isCollapsed
                    ? t('bookingSummary:showFullSummaryButtonLabel')
                    : t('bookingSummary:hideFullSummaryButtonLabel')}
                  <span
                    css={css({
                      marginInlineStart: '7px',
                      display: 'inline-block',
                    })}
                  >
                    <DropdownIndicator isFlipped={!isCollapsed} />
                  </span>
                </BodySmall>
              </AnchorButton>
            </Flex>
          </SummarySectionContainer>
          {/*<Spacer s="xs" /> //todo add a spacer for this */}
          <motion.div
            animate={{
              height: isCollapsed ? 0 : 'auto',
            }}
            initial={{
              height: isCollapsed ? 0 : 'auto',
            }}
            aria-hidden={isCollapsed}
          >
            <Divider />
            <BookingBreakdownRoomsTable
              {...props}
              wrapElementsInContainer
              pricePerNightMode="average"
              displayConfirmationNumber={false}
              separator={
                <>
                  <Divider />
                </>
              }
            />
            {showTaxTotalInBreakdown && (
              <>
                <Divider />
                <SummarySectionContainer>
                  <BookingBreakdownTaxTotalsTable {...props} />
                </SummarySectionContainer>
              </>
            )}
          </motion.div>
          <Divider />
          <div
            css={css`
              /* Raise this above the elements above for clean animation */
              z-index: 100;
              position: relative;
            `}
          >
            <SummarySectionContainer>
              {showStayMembershipOverview ? (
                <DataTableRow
                  title={t('bookingMembership:summary.payment.title')}
                  label={t('bookingMembership:summary.payment.label')}
                  description={t(
                    'bookingMembership:summary.payment.description'
                  )}
                  value={<CurrencyPreview amount={props.overallTotal} />}
                  testId="membership-total"
                  valueFontWeight="bold"
                />
              ) : (
                <DataTableRow
                  label={t('bookingSummary:labels.total')}
                  value={<CurrencyPreview amount={props.overallTotal} />}
                  testId="total"
                  valueFontWeight={
                    componentProperties.staySummary?.total?.value?.fontWeight
                  }
                />
              )}

              <AlternativeCurrencyCheckoutWarning
                total={props.overallTotal}
                css={
                  showStayMembershipOverview && {
                    fontSize: '12px',
                    opacity: 0.56,
                  }
                }
              />
            </SummarySectionContainer>

            <Divider />

            {showAveragePricePerNight &&
              props.numberOfNights > 1 &&
              props.totalAverageNightlyAmountAfterTax && (
                <SummarySectionContainer>
                  <DataTableRow
                    label={t('bookingSummary:labels.room.pricePerNight')}
                    value={
                      <CurrencyPreview
                        amount={props.totalAverageNightlyAmountAfterTax}
                      />
                    }
                  />
                </SummarySectionContainer>
              )}

            {displayCityTaxOverride ? (
              <>
                <Divider />
                <SummarySectionContainer>
                  <CityTaxLineItemOverride hotelReferenceId={props.hotelSlug} />
                </SummarySectionContainer>
              </>
            ) : null}
          </div>
          {showStayMembershipOverview && membershipPrice && (
            <MembershipSummary membershipPrice={membershipPrice} />
          )}
        </div>
      </div>
      {showStayMembershipOverview && (
        <MembershipAlert amountSaved={discountDetail.amountSaved} />
      )}
    </section>
  );
};

export default YourStaySummary;
